
import { Task, TaskChangeRequest } from "../../types/tasks";
import { toast } from "sonner";
import { v4 as uuidv4 } from 'uuid';

export const useTaskChangeRequests = (
  tasks: Task[], 
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>,
  selectedTask: Task | null,
  setSelectedTask: React.Dispatch<React.SetStateAction<Task | null>>
) => {
  // Request changes to a task (reassignment or deadline extension)
  const handleRequestChange = (taskId: string, requestType: "reassign" | "extend", details: string, userId?: string) => {
    if (!userId) return;
    
    // Find the task
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    // Create a change request
    const changeRequest: TaskChangeRequest = {
      id: uuidv4(),
      taskId,
      requestType,
      requesterId: userId,
      details,
      status: "pending",
      createdAt: new Date().toISOString()
    };
    
    // Add the change request to the task and set hasPendingRequests flag
    const updatedTask = {
      ...task,
      changeRequests: [...(task.changeRequests || []), changeRequest],
      hasPendingRequests: true
    };
    
    // Update the tasks array
    setTasks(tasks.map(t => t.id === taskId ? updatedTask : t));
    
    // Also update the selected task
    if (selectedTask && selectedTask.id === taskId) {
      setSelectedTask(updatedTask);
    }
    
    toast.success(`${requestType === 'reassign' ? 'Reassignment' : 'Deadline extension'} request submitted`);
  };

  // Approve or reject change request
  const handleChangeRequestResponse = (taskId: string, requestId: string, approve: boolean) => {
    // Find the task
    const task = tasks.find(t => t.id === taskId);
    if (!task || !task.changeRequests) return;
    
    // Find the specific request
    const request = task.changeRequests.find(r => r.id === requestId);
    if (!request) return;
    
    let updatedTask: Task;
    
    if (approve) {
      // Apply the requested changes
      if (request.requestType === "reassign") {
        // For reassignment, we would need actual assignee info
        // This is simplified - in a real app, you'd select from available users
        updatedTask = {
          ...task,
          assignee: {
            id: "user4", // This would come from the UI selection
            name: "Dave Wilson",
            avatar: "https://i.pravatar.cc/150?u=dave"
          },
          changeRequests: task.changeRequests.map(r => 
            r.id === requestId ? { ...r, status: "approved" } : r
          )
        };
        toast.success("Task reassigned successfully");
      } else if (request.requestType === "extend") {
        // For extending deadline, we would need specific dates
        // This is simplified - in a real app, you'd set an actual new date
        const newDeadline = new Date();
        newDeadline.setDate(newDeadline.getDate() + 14); // Add 14 days
        
        updatedTask = {
          ...task,
          dueDate: `${newDeadline.toLocaleString('default', { month: 'long' })} ${newDeadline.getDate()}, ${newDeadline.getFullYear()}`,
          changeRequests: task.changeRequests.map(r => 
            r.id === requestId ? { ...r, status: "approved" } : r
          )
        };
        toast.success("Deadline extended successfully");
      } else {
        // Just mark the request as approved without changes
        updatedTask = {
          ...task,
          changeRequests: task.changeRequests.map(r => 
            r.id === requestId ? { ...r, status: "approved" } : r
          )
        };
        toast.success("Request approved");
      }
    } else {
      // Just mark the request as rejected
      updatedTask = {
        ...task,
        changeRequests: task.changeRequests.map(r => 
          r.id === requestId ? { ...r, status: "rejected" } : r
        )
      };
      
      toast.error("Request rejected");
    }
    
    // Check if there are still pending requests
    const stillHasPendingRequests = updatedTask.changeRequests.some(r => r.status === "pending");
    updatedTask.hasPendingRequests = stillHasPendingRequests;
    
    // Update the tasks array
    setTasks(tasks.map(t => t.id === taskId ? updatedTask : t));
    
    // Also update the selected task
    if (selectedTask && selectedTask.id === taskId) {
      setSelectedTask(updatedTask);
    }
  };

  // Count total pending requests across all tasks for admin notification
  const getPendingRequestsCount = (taskList: Task[]) => {
    return taskList.reduce((count, task) => {
      const taskPendingRequests = task.changeRequests?.filter(req => req.status === "pending").length || 0;
      return count + taskPendingRequests;
    }, 0);
  };

  return {
    handleRequestChange,
    handleChangeRequestResponse,
    getPendingRequestsCount
  };
};
